import { z } from 'zod';
import { UserIdSchema, RoleIdSchema } from './common';

/**
 * Roles de la plataforma. Cada rol mapea a un conjunto cerrado de permisos
 * en el backend (RBAC). NO añadir roles sin actualizar el guard correspondiente.
 */
export const RoleSchema = z.enum([
  'admin',
  'medico',
  'anestesiologo',
  'enfermera',
  'recepcion',
  'auditoria',
]);
export type Role = z.infer<typeof RoleSchema>;

export const RoleLabel: Record<Role, string> = {
  admin: 'Administrador',
  medico: 'Médico tratante',
  anestesiologo: 'Anestesiólogo',
  enfermera: 'Enfermera',
  recepcion: 'Recepción / Admisiones',
  auditoria: 'Consulta / Auditoría',
};

/** Etiqueta del súper administrador (admin protegido, `esSuperAdmin=true`). */
export const SUPER_ADMIN_LABEL = 'Súper Administrador';

/**
 * Etiqueta de rol para MOSTRAR, considerando el flag de súper admin.
 * Un `admin` con `esSuperAdmin=true` se muestra como "Súper Administrador";
 * el resto usa `RoleLabel[rol]` (un admin normal → "Administrador").
 */
export function roleLabelFor(user: {
  rol: string;
  esSuperAdmin?: boolean | undefined;
}): string {
  if (user.rol === 'admin' && user.esSuperAdmin === true) return SUPER_ADMIN_LABEL;
  // Built-in → etiqueta fija; perfil personalizado → el backend/lista de roles
  // provee el nombre real (fallback: el propio slug).
  return RoleLabel[user.rol as Role] ?? user.rol;
}

export const UserStatusSchema = z.enum(['activo', 'inactivo', 'bloqueado']);
export type UserStatus = z.infer<typeof UserStatusSchema>;

export const UserSchema = z.object({
  id: UserIdSchema,
  nombre: z.string().min(2).max(150),
  correo: z.string().email().max(254),
  // Id de rol: built-in (`admin`, `medico`, …) o de un perfil personalizado.
  rol: RoleIdSchema,
  estado: UserStatusSchema,
  especialidad: z.string().max(120).optional(),
  registroProfesional: z.string().max(50).optional(),
  /** Cédula de ciudadanía (Colombia). Se imprime en los bloques de firma. */
  cedula: z.string().max(30).optional(),
  /**
   * Ruta relativa de la imagen de firma pre-cargada del profesional, SIEMPRE
   * dentro de `uploads/signatures/`. El generador de PDF la lee del disco con
   * contención de directorio; el patrón estricto evita path traversal.
   */
  firmaUrl: z
    .string()
    .max(500)
    .regex(/^uploads\/signatures\/[\w.-]+\.(png|jpe?g)$/i, 'Ruta de firma inválida')
    .optional(),
  /**
   * `true` = cuenta de acceso (puede autenticarse): admin/recepción/auditoría y
   * la cuenta compartida de enfermería. `false` = personal de directorio
   * (médicos, anestesiólogos y cada enfermera individual): existe como entidad
   * firmante/payroll pero no inicia sesión. Default `true` en backend.
   */
  puedeIniciarSesion: z.boolean().optional(),
  /**
   * `true` = SÚPER administrador (cuenta protegida del dueño). Un admin normal
   * NO puede editar, cambiar la contraseña ni desactivar a un súper admin.
   * Es de SOLO LECTURA por API: se omite de create/update para evitar escalada
   * de privilegios; solo se asigna vía seed/migración.
   */
  esSuperAdmin: z.boolean().optional(),
  ultimoIngreso: z.string().datetime().optional(),
});
export type User = z.infer<typeof UserSchema>;

export const CreateUserSchema = UserSchema.omit({
  id: true,
  ultimoIngreso: true,
  esSuperAdmin: true,
}).extend({
  password: z
    .string()
    .min(12, 'Mínimo 12 caracteres')
    .max(128)
    .regex(/[A-Z]/, 'Requiere al menos una mayúscula')
    .regex(/[a-z]/, 'Requiere al menos una minúscula')
    .regex(/\d/, 'Requiere al menos un dígito')
    .regex(/[^A-Za-z0-9]/, 'Requiere al menos un carácter especial'),
});
export type CreateUserInput = z.infer<typeof CreateUserSchema>;

/**
 * Update — el `id` viaja en el path param, NO en el body. `ultimoIngreso`
 * es derivado por el backend (login). El resto es parcial.
 */
export const UpdateUserSchema = UserSchema.omit({
  id: true,
  ultimoIngreso: true,
  esSuperAdmin: true,
}).partial();
export type UpdateUserInput = z.infer<typeof UpdateUserSchema>;

/**
 * Reseteo de contraseña por un administrador (POST /users/:id/password). Mismas
 * reglas de robustez que en la creación. El usuario podrá seguir usándola tal
 * cual (no se fuerza cambio en el próximo inicio).
 */
export const AdminSetPasswordSchema = z.object({
  password: z
    .string()
    .min(12, 'Mínimo 12 caracteres')
    .max(128)
    .regex(/[A-Z]/, 'Requiere al menos una mayúscula')
    .regex(/[a-z]/, 'Requiere al menos una minúscula')
    .regex(/\d/, 'Requiere al menos un dígito')
    .regex(/[^A-Za-z0-9]/, 'Requiere al menos un carácter especial'),
});
export type AdminSetPasswordInput = z.infer<typeof AdminSetPasswordSchema>;

/**
 * Matriz rol → permisos lógicos. Fuente única de verdad para el RBAC tanto
 * en backend (cálculo del `SessionUser.permisos`) como en frontend (gating de UI).
 * Mantener sincronizado con los decoradores `@Roles(...)` de cada controller.
 *
 * Los permisos siguen el patrón `<recurso>:<accion>`. `*` significa "todos".
 */
export const PERMISSIONS_BY_ROLE: Record<Role, readonly string[]> = {
  admin: ['*'],
  medico: [
    'patients:read',
    'patients:write',
    'procedures:read',
    'procedures:write',
    'consent:sign',
  ],
  anestesiologo: [
    'patients:read',
    'procedures:read',
    'anesthesia:sign',
  ],
  enfermera: [
    'patients:read',
    'procedures:read',
    'expense:write',
    'expense:sign',
  ],
  recepcion: [
    'patients:read',
    'patients:write',
    'procedures:read',
    'procedures:write',
  ],
  auditoria: [
    'patients:read',
    'procedures:read',
    'audit:read',
    'users:read',
  ],
} as const;
